import type { ReactNode } from "react";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { Logo } from "@/components/app/logo";
import { Card } from "@/components/ui/card";
import { hasAccounts, publicDemo, signsIn } from "@/lib/account";
import { matchPath, useLocation } from "@/lib/pages";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { RecoveryKeyOfferPage } from "../keys/keys";
import { DemoRequestPage } from "./demo";
import { InvitePage } from "./invite";
import { PasskeyOfferPage } from "./passkey-offer";
import { ProfilePage, RestrictedPage } from "./profile";
import { ForgotPage, ResetPage, SignInPage } from "./sign-in";
import { UserPicker } from "./user-picker";
import { WelcomePage } from "./welcome";

/**
 * /account/…: signing in, password reset, invitations, the welcome steps, the offer to add a
 * passkey, the profile and the restricted state (ADR 0011), and the public demo's request page (ADR 0012). On the desktop app
 * with users (ADR 0013), the user picker, the welcome steps and the profile. Where nobody signs
 * in, the address leads back to the garages.
 */
export function AccountApp() {
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const path = pathname.replace(/^\/account/, "") || "/";

  useEffect(() => {
    if (!signsIn()) window.location.replace("/");
  }, []);
  if (!signsIn()) return null;

  const invite = hasAccounts() ? matchPath("/invite/:token", path) : null;
  const reset = hasAccounts() ? matchPath("/reset/:token", path) : null;
  let page: ReactNode;
  if (invite) page = <InvitePage token={invite.token ?? ""} />;
  else if (reset) page = <ResetPage token={reset.token ?? ""} />;
  else if (path === "/forgot" && hasAccounts()) page = <ForgotPage />;
  else if (path === "/profile") page = <ProfilePage />;
  else if (path === "/restricted") page = <RestrictedPage />;
  else if (path === "/welcome") page = <WelcomePage />;
  else if (path === "/passkey" && hasAccounts()) page = <PasskeyOfferPage />;
  else if (path === "/recovery-key" && hasAccounts()) page = <RecoveryKeyOfferPage />;
  else if (path === "/demo" && publicDemo()) page = <DemoRequestPage />;
  else page = hasAccounts() ? <SignInPage /> : <UserPicker />;

  return (
    <>
      {page}
      <Toaster
        theme={theme}
        position="bottom-center"
        toastOptions={{
          classNames: {
            toast: "!rounded-xl !border-border !bg-surface-raised !text-foreground !shadow-lg",
          },
        }}
      />
    </>
  );
}

/** A centered card with the logo above it: the sign-in, reset and invitation pages. */
export function AuthCard({
  title,
  body,
  children,
  wide = false,
}: {
  title: ReactNode;
  body?: ReactNode;
  children?: ReactNode;
  wide?: boolean;
}) {
  return (
    <main className="flex min-h-dvh flex-col items-center bg-background px-4 pt-[max(3rem,env(safe-area-inset-top))] pb-12 sm:justify-center sm:pt-12">
      <a
        href="/"
        className="mb-8 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        <Logo />
      </a>
      <Card className={cn("w-full p-5 sm:p-6", wide ? "max-w-lg" : "max-w-sm")}>
        <h1 className="text-xl font-semibold tracking-tight text-balance">{title}</h1>
        {body ? (
          <div className="mt-1.5 text-sm text-pretty text-muted-foreground">{body}</div>
        ) : null}
        {children ? <div className="mt-5">{children}</div> : null}
      </Card>
    </main>
  );
}
